import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Grid, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Paper, InputAdornment, Chip, FormControlLabel, Checkbox } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import PaidIcon from '@mui/icons-material/Paid';
import { useEffect, useState } from 'react';
import TablePagination from '@mui/material/TablePagination';
import useInvite from './useInvite';
import { downloadInviteDataCSV, issuedInvite, retrieveReferralPool } from 'src/pages/admin/services/whitelistService';

export interface InvitationRecord {
  referrerId: string;
  referrerAddress: string;
  email: string;
  totalInvitations: number;
  validCount: number;
  computedReward: string;
  state: string;
  payoutStatus: string | null;
}

export interface InvitationSummary {
  totalUsers: number;
  totalInvitations: number;
  totalValidInvitations: number;
  rewardsShouldBeIssuedUsdt: string;
}

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { retrieve, records, total, retrieveSummary, summary } = useInvite();
  // Draft states — bound to inputs, do NOT trigger API
  const [searchInput, setSearchInput] = useState('');
  const [showOnlyValid, setShowOnlyValid] = useState(false);
  const [notDistributed, setNotDistributed] = useState(false);
  // Committed states — updated only on Search click, trigger API via useEffect
  const [committedSearch, setCommittedSearch] = useState('');
  const [committedShowOnlyValid, setCommittedShowOnlyValid] = useState(false);
  const [committedNotDistributed, setCommittedNotDistributed] = useState(false);
  const [referralPoolTotal, setReferralPoolTotal] = useState<string | null>(null);

  useEffect(() => {
    retrieve(page + 1, rowsPerPage, committedSearch || undefined, committedShowOnlyValid || undefined, committedNotDistributed || undefined);
  }, [page, rowsPerPage, committedSearch, committedShowOnlyValid, committedNotDistributed, retrieve]);

  const handleSearch = () => {
    setPage(0);
    setCommittedSearch(searchInput.trim());
    setCommittedShowOnlyValid(showOnlyValid);
    setCommittedNotDistributed(notDistributed);
  };

  useEffect(() => {
    retrieveSummary();
    retrieveReferralPool()
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setReferralPoolTotal(data?.totalUsdc ?? null);
      })
      .catch((err) => console.log('referral pool error:', err));
  }, [retrieveSummary]);

  const unpaidRecords = records.filter((r) => r.payoutStatus !== 'PAID');

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(unpaidRecords.map((r) => r.referrerId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleBatchDistribute = async () => {
    try {
      const response = await issuedInvite(Array.from(selectedIds));
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
      setSelectedIds(new Set());
      await retrieve(page + 1, rowsPerPage, committedSearch || undefined, committedShowOnlyValid || undefined, committedNotDistributed || undefined);
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const exportToCSV = async() => {
    try {
      const response = await downloadInviteDataCSV(committedSearch || undefined, committedShowOnlyValid || undefined, committedNotDistributed || undefined);

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'leaderboard_rewards.csv');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download CSV:', error);
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-invite" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          {translate('whitelist.inviteData.title')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.inviteData.participants')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.totalUsers : 0} {translate('whitelist.personUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.inviteData.totalInvite')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.totalInvitations : 0} {translate('whitelist.inviteData.time')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.inviteData.totalValidInvite')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {summary ? summary.totalValidInvitations : 0}{' '}
                  {translate('whitelist.inviteData.time')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.inviteData.dueInviteReward')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {summary ? summary.rewardsShouldBeIssuedUsdt : 0} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.referralPool.totalUsdc')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {referralPoolTotal ?? '-'} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper
          sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <TextField
            placeholder={translate('whitelist.inviteData.search')}
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flexGrow: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyValid}
                onChange={(e) => setShowOnlyValid(e.target.checked)}
              />
            }
            label={translate('whitelist.inviteData.showOnlyValidInvites')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={notDistributed}
                onChange={(e) => setNotDistributed(e.target.checked)}
              />
            }
            label={translate('whitelist.inviteData.showOnlyNotDistributed')}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            {translate('whitelist.users.search')}
          </Button>
          <PermissionGuard resource="whitelist-invite" action="export" fallback={null}>
            <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportToCSV}>
              {translate('whitelist.exportCSV')}
            </Button>
          </PermissionGuard>
        </Paper>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2">
            {translate('whitelist.leaderboard.itemSelected', { selectedCount: selectedIds.size })}
          </Typography>
          <PermissionGuard resource="whitelist-invite" action="distribute" fallback={null}>
            <Button
              variant="contained"
              startIcon={<PaidIcon />}
              onClick={handleBatchDistribute}
              disabled={selectedIds.size === 0}
            >
              {translate('whitelist.leaderboard.batchIssue')} ({selectedIds.size})
            </Button>
          </PermissionGuard>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < unpaidRecords.length}
                    checked={selectedIds.size === unpaidRecords.length && unpaidRecords.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{translate('whitelist.inviteData.inviter')}</TableCell>
                <TableCell>{translate('whitelist.inviteData.email')}</TableCell>
                <TableCell align="right">{translate('whitelist.inviteData.totalInvite')}</TableCell>
                <TableCell align="right">{translate('whitelist.inviteData.validInvite')}</TableCell>
                <TableCell align="right">{translate('whitelist.inviteData.rewardDue')}</TableCell>
                <TableCell align="center">{translate('whitelist.inviteData.statue')}</TableCell>
                <TableCell align="center">{translate('whitelist.inviteData.payoutStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.referrerId} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(record.referrerId)}
                      disabled={record.payoutStatus === 'PAID'}
                      onChange={() => handleSelectOne(record.referrerId)}
                    />
                  </TableCell>
                  <TableCell>{record.referrerAddress}</TableCell>
                  <TableCell>{record.email}</TableCell>
                  <TableCell align="right">{record.totalInvitations}</TableCell>
                  <TableCell align="right">{record.validCount}</TableCell>
                  <TableCell align="right">
                    {Number(record.computedReward) > 0 ? `${record.computedReward} USDT` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        record.state !== 'not_reached'
                          ? translate('whitelist.inviteData.achieved')
                          : translate('whitelist.inviteData.unachieved')
                      }
                      color={record.state !== 'not_reached' ? 'success' : 'default'}
                      size="small"
                      variant={record.state !== 'not_reached' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {record.payoutStatus == null ? (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    ) : (
                      <Chip
                        label={
                          record.payoutStatus 
                        }
                        color={record.payoutStatus === 'PAID' ? 'success' : 'default'}
                        size="small"
                        variant={record.payoutStatus === 'PAID' ? 'filled' : 'outlined'}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>

      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
